import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/work", label: "Work", testid: "nav-work-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-border/70 bg-background/80 backdrop-blur-md" data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="font-serif text-xl tracking-tight">
          Twinkle Rajan<span className="text-accent">.</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-accent ${
                location.pathname.startsWith(l.to) ? "text-accent" : "text-foreground/70"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label="Toggle dark mode"
            className="p-2 rounded-full border border-border hover:border-accent hover:text-accent transition-colors duration-300"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </nav>
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button-mobile"
            aria-label="Toggle dark mode"
            className="p-2 rounded-full border border-border"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            className="p-2"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-border bg-background overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className="font-serif text-2xl tracking-tight"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
